/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { Button } from 'reactstrap';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '../../../../components/dashboard';
import SurveyQuestionList from '../../../../components/survey-question-list/survey-question-list';
import { useGetSurveyQuery, useLazyGenerateScoresQuery } from '../../api/materiality-assessment';
import { useNavigate } from 'react-router-dom-v5-compat';
import { getV6Router } from '../../../common/utils/V6Router';
import { V6Route } from '../../../common/types/V6Router';
import { useSurveyGroups } from '../../../../utils/survey';
import { SurveyOverviewMode } from '../../../../slice/surveySettings';
import { ColumnTitle } from '../../../../components/survey-question-list/partials/ColumnTitle';
import { ColumnSimpleStatusIcon } from '../../../../components/survey-question-list/partials/ColumnSimpleStatusIcon';
import { useAppSelector } from '../../../../reducers';
import { isStaff } from '../../../../selectors/user';
import IconButton from '../../../../components/button/IconButton';
import { AssessmentProgress } from './AssessmentProgress';
import { BasicAlert } from '../../../../components/alert/BasicAlert';
import { QueryWrapper } from '../../../../components/query/QueryWrapper';
import { AssessmentResults } from './AssessmentResults';
import './styles.scss';

interface AssessmentProps {
  initiativeId: string;
  surveyId: string;
};

const router = getV6Router();
const NoData = ({ text }: { text: string }) => <div className='mr-3'><BasicAlert type='warning'>{text}</BasicAlert></div>;

export const Assessment = (props: AssessmentProps) => {
  const { initiativeId, surveyId } = props;
  const navigate = useNavigate();

  const userIsStaff = useAppSelector(isStaff);
  const surveyQuery = useGetSurveyQuery({ surveyId });
  const surveyGroups = useSurveyGroups(SurveyOverviewMode.Universal, surveyQuery.data);
  const [getScores, { data }] = useLazyGenerateScoresQuery();
  const scores = data?.scores;

  const handleSubmit = () => {
    getScores({ initiativeId, surveyId });
  };
  const handleGoToTestingTool = () => navigate(
    router.generateUrl(V6Route.MaterialityAssessmentTestingTool, { initiativeId })
  );
  const handleBackToList = () => {
    const url = router.generateUrl(V6Route.MaterialityAssessmentSurveysList, { initiativeId });
    navigate(url);
  }
  const handleGoToQuestion = (questionId: string) => {
    const url = router.generateUrl(V6Route.MaterialityAssessmentSurveyQuestion, { initiativeId, surveyId, questionId });
    navigate(url);
  };

  return (
    <Dashboard className='assessmentContainer'>
      <DashboardRow>
        <div className='d-flex flex-fill align-items-center'>
          <div className='me-auto'>
            <Button color='link' onClick={handleBackToList}>
              <i className='fal fa-chevron-left me-2' />
              Back to assessment list
            </Button>
          </div>
          <div>
            <Button onClick={handleSubmit} className='me-2'>Submit assessment</Button>
            {userIsStaff ? <IconButton color='primary' outline icon='fal fa-cog' onClick={handleGoToTestingTool} /> : null}
          </div>
        </div>
      </DashboardRow>
      <DashboardRow>
        <AssessmentProgress surveyGroups={surveyGroups} />
      </DashboardRow>
      {scores ?
        <DashboardSection title='Assessment Results'>
          <AssessmentResults scores={scores} />
        </DashboardSection> :
        <QueryWrapper
          query={surveyQuery}
          onError={() => <NoData text='There was an error fetching your assessments. Please try again.' />}
          onNoData={() => <NoData text='No assessments currently available.' />}
          onSuccess={(survey) => (
            <>
              <DashboardSectionTitle title={`Assessment ${survey.name}`} />
              <DashboardSection>
                <SurveyQuestionList
                  surveyId={surveyId}
                  initiativeId={initiativeId}
                  surveyGroups={surveyGroups}
                  goToQuestion={handleGoToQuestion}
                  columns={[
                    ColumnTitle,
                    ColumnSimpleStatusIcon,
                  ]}
                />
              </DashboardSection>
            </>
          )} />
      }
    </Dashboard>
  );
}
